import React, { useState, useEffect, useCallback } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import styled from "styled-components";
import { toast } from "react-toastify";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import flvjs from "flv.js";

import { Container } from "../layout/Container";
import Button from "../components/Button";
import useTranslation from "../hooks/useTranslation";
import {
  getReplayDetails,
  formatDuration,
  formatFileSize,
  formatRelativeTime,
  getReplayPlaybackUrl,
  getReplayThumbnailUrl
} from "../services/replaysApi";

// Icons
import { ReactComponent as EyeIcon } from "../img/icons/eye-on.svg";
import { ReactComponent as CalendarIcon } from "../img/icons/calender.svg";

const ReplayPlayer = () => {
  const { t } = useTranslation();
  const { replayId } = useParams();
  const history = useHistory();
  const [replay, setReplay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [player, setPlayer] = useState(null);
  const videoRef = React.useRef(null);

  const fitPlayerToViewport = useCallback((activePlayer) => {
    // Goal: ensure the bottom control bar stays visible without scrolling.
    // We clamp the player height to the remaining viewport height from its top position.
    const el = activePlayer && typeof activePlayer.el === "function"
      ? activePlayer.el()
      : videoRef.current;

    if (!el) return;

    const rect = el.getBoundingClientRect();
    const parentWidth = (el.parentElement && el.parentElement.clientWidth) ? el.parentElement.clientWidth : rect.width;

    // Leave a small gap at bottom so controls aren't flush to the edge.
    const bottomGap = 16;
    const maxHeight = Math.max(220, window.innerHeight - rect.top - bottomGap);

    // 16:9 sizing, but clamped to maxHeight.
    let width = Math.max(280, parentWidth);
    let height = (width * 9) / 16;
    if (height > maxHeight) {
      height = maxHeight;
      width = (height * 16) / 9;
    }

    const w = Math.floor(width);
    const h = Math.floor(height);

    // video.js player
    if (activePlayer && typeof activePlayer.width === "function" && typeof activePlayer.height === "function") {
      activePlayer.width(w);
      activePlayer.height(h);
      return;
    }

    // Native <video> (FLV fallback via flv.js)
    el.style.width = `${w}px`;
    el.style.height = `${h}px`;
  }, []);

  useEffect(() => {
    loadReplay();
    return () => {
      if (player) {
        // Check if it's a flv.js player or video.js player
        if (player.destroy) {
          player.destroy(); // flv.js
        } else if (player.dispose) {
          player.dispose(); // video.js
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayId]);

  useEffect(() => {
    if (!player && !videoRef.current) return;

    const onResize = () => {
      // Use rAF so layout is stable before measuring.
      window.requestAnimationFrame(() => fitPlayerToViewport(player));
    };

    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [player, fitPlayerToViewport]);

  const loadReplay = async () => {
    try {
      const data = await getReplayDetails(replayId);
      setReplay(data.replay);

      // Initialize video player after replay data is loaded
      setTimeout(() => initializePlayer(data.replay), 100);
    } catch (err) {
      console.error("Error loading replay:", err);
      toast.error(t('replay.failed_to_load'));
      history.push("/404");
    } finally {
      setLoading(false);
    }
  };

  const initializePlayer = (replayData) => {
    if (!videoRef.current || !replayData) return;

    const playbackUrl = getReplayPlaybackUrl(replayData.filePath);

    // Check if file is FLV
    const isFLV = replayData.filePath.toLowerCase().endsWith('.flv');

    if (isFLV && flvjs.isSupported()) {
      // Use flv.js for FLV playback
      console.log("🎥 Using flv.js for FLV playback:", playbackUrl);

      const flvPlayer = flvjs.createPlayer({
        type: 'flv',
        url: playbackUrl,
        isLive: false,
        hasAudio: true,
        hasVideo: true
      }, {
        enableWorker: false,
        enableStashBuffer: true,
        stashInitialSize: 128,
        autoCleanupSourceBuffer: true
      });

      flvPlayer.attachMediaElement(videoRef.current);
      flvPlayer.load();

      // Handle errors
      flvPlayer.on(flvjs.Events.ERROR, (errorType, errorDetail, errorInfo) => {
        console.error('FLV.js error:', errorType, errorDetail, errorInfo);
        toast.error(t('replay.error_loading_refresh'));
      });

      setPlayer(flvPlayer);

      // Add controls to the video element
      videoRef.current.controls = true;
      videoRef.current.poster = getReplayThumbnailUrl(replayData.thumbnailPath);
      window.requestAnimationFrame(() => fitPlayerToViewport(flvPlayer));

    } else if (isFLV && !flvjs.isSupported()) {
      // FLV is not supported in this browser
      toast.error(t('replay.browser_not_supported'), {
        autoClose: false
      });
    } else {
      // Use video.js for non-FLV files (MP4, etc.)
      console.log("🎥 Using video.js for playback:", playbackUrl);

      const playerInstance = videojs(videoRef.current, {
        controls: true,
        autoplay: false,
        preload: "auto",
        // We size the player to fit the viewport so controls stay visible.
        // (fluid/aspectRatio can produce a player taller than the viewport on large screens.)
        fluid: false,
        poster: getReplayThumbnailUrl(replayData.thumbnailPath),
        sources: [{
          src: playbackUrl,
          type: 'video/mp4'
        }],
        techOrder: ['html5']
      });

      playerInstance.on('error', () => {
        const error = playerInstance.error();
        console.error('Video.js error:', error);
        toast.error(t('replay.error_loading_download'));
      });

      setPlayer(playerInstance);
      window.requestAnimationFrame(() => fitPlayerToViewport(playerInstance));
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <p>{t('replay.loading')}</p>
        </LoadingContainer>
      </Container>
    );
  }

  if (!replay) {
    return (
      <Container>
        <ErrorContainer>
          <h2>{t('replay.not_found')}</h2>
          <Button onClick={() => history.push("/")}>{t('replay.go_home')}</Button>
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      <PlayerContainer>
        <VideoContainer>
          <video
            ref={videoRef}
            className="video-js vjs-big-play-centered"
          />
        </VideoContainer>

        <MetadataContainer>
          <Title>{replay.title}</Title>

          {replay.description && (
            <Description>{replay.description}</Description>
          )}

          <Stats>
            <StatItem>
              <EyeIcon style={{ width: "18px", height: "18px", marginRight: "6px" }} />
              {replay.viewCount || 0} {t('replay.views')}
            </StatItem>
            <StatItem>
              <CalendarIcon style={{ width: "18px", height: "18px", marginRight: "6px" }} />
              {formatRelativeTime(replay.createdAt)}
            </StatItem>
            {replay.duration && (
              <StatItem>
                {t('replay.duration')}: {formatDuration(replay.duration)}
              </StatItem>
            )}
            {replay.fileSize && (
              <StatItem>
                {t('replay.size')}: {formatFileSize(parseInt(replay.fileSize))}
              </StatItem>
            )}
          </Stats>

          {replay.stream_games && (
            <GameInfo>
              <GameLabel>{t('replay.game')}:</GameLabel>
              <Link to={`/game/${replay.stream_games.id}`}>
                <GameTag>
                  {replay.stream_games.icon && (
                    <img src={replay.stream_games.icon} alt={replay.stream_games.name} />
                  )}
                  <span>{replay.stream_games.name}</span>
                </GameTag>
              </Link>
            </GameInfo>
          )}

          {/* Download option */}
          <DownloadSection>
            <Button
              as="a"
              href={getReplayPlaybackUrl(replay.filePath)}
              download
              variant="secondary"
              size="small"
            >
              📥 {t('replay.download')} ({replay.fileSize && formatFileSize(parseInt(replay.fileSize))})
            </Button>
          </DownloadSection>
        </MetadataContainer>
      </PlayerContainer>
    </Container>
  );
};

// Styled Components
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 18px;
  color: ${({ theme }) => theme.color.text};
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  gap: 20px;

  h2 {
    color: ${({ theme }) => theme.color.text};
    margin: 0;
  }
`;

const PlayerContainer = styled.div`
  margin: 24px 0;
  background-color: ${({ theme }) => theme.color.secondaryBg};
  border-radius: 12px;
  overflow: hidden;

  @media (max-width: 768px) {
    margin: 16px 0;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  background-color: #000;
  display: flex;
  justify-content: center;

  .video-js {
    width: 100%;
  }

  .vjs-big-play-button {
    border-radius: 50%;
    width: 80px;
    height: 80px;
    line-height: 80px;
    border: none;
    background-color: ${({ theme }) => theme.color.primary};

    &:hover {
      background-color: ${({ theme }) => theme.color.primaryDark};
    }
  }
`;

const MetadataContainer = styled.div`
  padding: 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.text};
  margin: 0 0 16px 0;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const Description = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.color.darkerText};
  margin: 0 0 20px 0;
  line-height: 1.6;
`;

const Stats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.color.background};
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 15px;
  color: ${({ theme }) => theme.color.darkerText};

  svg {
    fill: ${({ theme }) => theme.color.darkerText};
  }
`;

const GameInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const GameLabel = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.text};
`;

const GameTag = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: ${({ theme }) => theme.color.background};
  border-radius: 20px;
  font-size: 14px;
  color: ${({ theme }) => theme.color.text};
  text-decoration: none;
  transition: background-color 0.2s ease;

  img {
    width: 24px;
    height: 24px;
    border-radius: 4px;
  }

  &:hover {
    background-color: ${({ theme }) => theme.color.primaryLight};
    color: ${({ theme }) => theme.color.primary};
  }
`;

const DownloadSection = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid ${({ theme }) => theme.color.background};
  
  @media (max-width: 768px) {
    text-align: center;
  }
`;

export default ReplayPlayer;

